import type { ReactNode } from 'react';
import { t, type Leaf, type Locale } from '../../i18n/strings';
import { cx } from '../../lib/cx';
import styles from './paint.module.css';

export interface MenuItem {
  readonly id: string;
  readonly label: Leaf;
  /** Raccourci affiché à droite de l'entrée, purement informatif. */
  readonly shortcut?: string;
  readonly disabled?: boolean;
  /** Coche à gauche, pour les entrées qui basculent un réglage. */
  readonly checked?: boolean;
  readonly onSelect: () => void;
}

export interface Menu {
  readonly id: string;
  readonly label: Leaf;
  readonly items: readonly MenuItem[];
}

interface Props {
  readonly menus: readonly Menu[];
  readonly open: string | null;
  readonly lang: Locale;
  readonly onOpen: (id: string | null) => void;
}

/**
 * La barre de menus et son menu déroulant.
 *
 * Un clic ouvre, un second referme, `Échap` referme aussi (géré par le skin) ;
 * survoler une autre entrée pendant qu'un menu est ouvert bascule dessus,
 * comme dans le vrai logiciel.
 */
export function PaintMenuBar({ menus, open, lang, onOpen }: Props): ReactNode {
  return (
    <div className={styles.menuBar}>
      {menus.map((menu) => (
        <div key={menu.id} className={styles.menuHolder}>
          <button
            type="button"
            className={cx(styles.menuTitle, open === menu.id && styles.menuTitleOpen)}
            aria-haspopup="true"
            aria-expanded={open === menu.id}
            onClick={() => {
              onOpen(open === menu.id ? null : menu.id);
            }}
            onPointerEnter={() => {
              if (open !== null && open !== menu.id) onOpen(menu.id);
            }}
          >
            {t(menu.label, lang)}
          </button>

          {open === menu.id && (
            <div className={styles.menuDrop} role="menu">
              {menu.items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="menuitem"
                  className={styles.menuItem}
                  disabled={item.disabled ?? false}
                  onClick={() => {
                    onOpen(null);
                    item.onSelect();
                  }}
                >
                  <span className={styles.menuCheck} aria-hidden="true">
                    {item.checked === true ? '✓' : ''}
                  </span>
                  <span>{t(item.label, lang)}</span>
                  {item.shortcut !== undefined && (
                    <span className={styles.menuShortcut}>{item.shortcut}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
